import { useEffect, useRef, useState } from "react";
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { getJournalEntry, postJournalEntry, type JournalEntry } from "@/lib/api";

/** Never recreate or automatically retry a financial write after an uncertain response. */
export function ManualJournalPosting({ entry, lockDate, onEntry }: {
  entry: JournalEntry; lockDate?: string; onEntry: (entry: JournalEntry) => void;
}) {
  const [pending, setPending] = useState<JournalEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [needsCheck, setNeedsCheck] = useState(false);
  const [message, setMessage] = useState("");
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const eligible = entry.status === "draft" && /^[a-f0-9]{64}$/.test(entry.posting_review_sha256 ?? "");
  const locked = !!lockDate && entry.entry_date.slice(0, 10) <= lockDate.slice(0, 10);
  const post = async () => {
    if (!pending || inFlight.current) return;
    inFlight.current = true; setBusy(true);
    try {
      const result = await postJournalEntry(pending.id, pending.posting_review_sha256!);
      if (!mounted.current) return;
      if (result.id !== pending.id || result.status !== "posted" || result.legal_entity_id !== pending.legal_entity_id || result.organization_id !== pending.organization_id) {
        throw new Error("The response did not confirm this journal's posting.");
      }
      onEntry(result); setMessage(`Journal posted. ${result.entry_number}`);
    } catch {
      if (!mounted.current) return;
      setNeedsCheck(true);
      setMessage("Posting was not confirmed. The draft may have changed, the period may be locked, or the response may have been interrupted. Check current status before deciding what to do next.");
    } finally {
      if (mounted.current) { setPending(null); setBusy(false); }
      inFlight.current = false;
    }
  };
  const check = async () => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true);
    try {
      const current = await getJournalEntry(entry.id);
      if (!mounted.current) return;
      if (current.id !== entry.id || current.organization_id !== entry.organization_id || current.legal_entity_id !== entry.legal_entity_id) throw new Error("scope mismatch");
      onEntry(current); setNeedsCheck(false);
      setMessage(`Current status: ${current.status}. Review the journal and history before taking further action.`);
    } catch {
      if (!mounted.current) return;
      setMessage("Current status could not be checked. Posting remains blocked; try the status check again.");
    } finally { if (mounted.current) setBusy(false); inFlight.current = false; }
  };
  return <div className="grid gap-3">
    {eligible ? <Button type="button" disabled={busy || needsCheck || locked} onClick={() => { setMessage(""); setPending(entry); }}>Post draft</Button> : null}
    {eligible && locked ? <p role="status">This journal date is in a locked period.</p> : null}
    {message ? <p role="status">{message}</p> : null}
    {needsCheck ? <Button type="button" variant="secondary" disabled={busy} onClick={check}>Check current status</Button> : null}
    <Dialog open={pending !== null} onOpenChange={open => { if (!open && !busy) setPending(null); }} title="Post this draft journal?" description="Posts this existing draft to its books. Does not create another journal, move money, or lock the period.">
      {pending ? <div className="grid gap-4">
        <p>{pending.entry_number} · {pending.entry_date.slice(0, 10)} · {pending.memo}</p>
        <p>Balanced total: {formatMoney({ cents: pending.total_debit_cents, currency: "USD" })}</p>
        <div className="actions-row">
          <Button type="button" variant="secondary" disabled={busy} onClick={() => setPending(null)}>Cancel</Button>
          <Button type="button" disabled={busy} onClick={post}>{busy ? "Posting…" : "Post journal"}</Button>
        </div>
      </div> : null}
    </Dialog>
  </div>;
}
